<template>
  <div class="portlet light">
    <div class="portlet-title tabbable-line">
      <div class="caption caption-md">
        <i class="icon-globe theme-font hide"></i>
        <span class="caption-subject bold uppercase">MANAGE ACCOUNT</span>
      </div>
      <ul class="nav nav-tabs">
        <li :class="{active: activeTab == 'account-profile'}">
          <a href="#account-profile" data-toggle="tab" aria-expanded="false">Account Profile</a>
        </li>
        <li :class="{'disabled': $route.params.casemgrid == null, 'active' : activeTab == 'manage-users'}">
          <a href="#update-access" data-toggle="tab" aria-expanded="false">Manage Users</a>
        </li>
        <li :class="{active: activeTab == 'linked-accounts'}"
            v-if="accounts.accountType == 'parent' && accounts.id">
          <a href="#linked-accounts" data-toggle="tab" aria-expanded="false">Linked Accounts</a>
        </li>
        <li :class="{active: activeTab == 'programs'}"
            v-if="accounts.accountType == 'program' && accounts.id">
          <a href="#programs" data-toggle="tab" aria-expanded="false">Programs</a>
        </li>
      </ul>
    </div>
    <div class="tab-content marginTop">
      <div class="tab-pane" :class="{active: activeTab == 'account-profile'}" id="account-profile">
        <form data-vv-scope="ap" @submit.prevent="onSubmit">

          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label for="account-casemgr-url" class="control-label">CaseMGR URL:</label>
                <input id="account-casemgr-url" type="text" class="form-control" v-model="accounts.data.accountUrl" readonly="readonly">
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label for="account-activation-date" class="control-label">Activation Date:</label>
                <date-picker id="account-activation-date"
                             v-model="activationDate"
                             :disabled="true"
                             lang="en"
                             :value-type="'format'"
                             :confirm="false"
                             prefix-class="xmx"
                             :format="calendarFormat"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label for="account-system-id" class="control-label">System ID:</label>
                <input id="account-system-id" type="text" class="form-control" v-model="accounts.systemId" readonly="readonly">
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.status') }">
                <label for="account-status" class="control-label">Status: </label>
                <select class="form-control" id="account-status" v-model="accounts.status" name="status" data-vv-as="Status"
                        v-validate="'required'" :disabled="accounts.main">
                  <option>Active</option>
                  <option>Disabled</option>
                </select>
                <span v-show="errors.has('ap.status')" class="help-block">{{ errors.first('ap.status') }}</span>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.usersLimit') }">
                <label for="account-users-limit" class="control-label">* Users limit:</label>
                <input id="account-users-limit"
                       type="text"
                       class="form-control"
                       v-validate="'required|numeric|min_value:0'"
                       name="usersLimit"
                       data-vv-as="Users limit"
                       v-model="accounts.usersLimit">
                <span v-show="errors.has('ap.usersLimit')" class="help-block">{{ errors.first('ap.usersLimit') }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.participantType') }">
                <label for="account-participant-type" class="control-label">* Participant Type:</label>
                <select class="form-control" id="account-participant-type" v-model="accounts.participantType" name="participantType" data-vv-as="participantType"
                        v-validate="'required'" :disabled="accounts.id || accounts.accountType == 'child'" @change="updateAllAccountsList(true)">
                  <option value="1">Entity</option>
                  <option value="0">Individual</option>
                </select>
                <span v-show="errors.has('ap.participantType')" class="help-block">{{ errors.first('ap.participantType') }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.participantDisplayName') }">
                <label for="account-participant-type" class="control-label">* Participant Display Name:</label>
                <select class="form-control" id="account-participant-display" v-model="accounts.participantDisplayName" name="participantDisplayName" data-vv-as="participantDisplayName"
                        v-validate="'required'">
                  <option
                    v-for="displayName in participantDisplayNames"
                    :key="displayName.id"
                    :value="displayName.id">
                    {{ displayName.singular }}
                  </option>
                </select>
                <span v-show="errors.has('ap.participantType')" class="help-block">{{ errors.first('ap.participantType') }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.accountType') }">
                <label for="account-participant-type" class="control-label">* Account Type:</label>
                <select class="form-control" id="account-account-type" v-model="accounts.accountType" name="accountType" data-vv-as="accountType" v-validate="'required'" :disabled="lockAccountType" @change="updateAllAccountsList">
                  <option value="default">Undefined</option>
                  <option value="parent">Linked Accounts - Parent</option>
                  <option value="program">Program</option>
                  <option value="child" v-if="accounts.accountType == 'child'">Linked Accounts - Child</option>
                </select>
                <span v-show="errors.has('ap.participantType')" class="help-block">{{ errors.first('ap.participantType') }}</span>
              </div>
            </div>
            <div class="col-md-3" v-if="accounts.accountType == 'child'">
              <div class="form-group">
                <label for="account-parent-account" class="control-label">Parent Account:</label>
                <input id="account-parent-account" type="text" class="form-control" :value="accounts.accountType == 'child' ? (parentAccount ? parentAccount.organizationName : accounts.parentAccount.organizationName) : ''"
                       readonly="readonly">
              </div>
            </div>
            <div class="col-md-3" v-if="accounts.accountType == 'program' || accounts.accountType =='default'">

              <div class="form-group" v-if="accounts.accountType == 'program' || accounts.accountType =='default'" :class="{ 'has-error': errors.has('ap.participantType') }">
                <label>Participant search in following organizations:</label>

                <vue-select
                    v-model="accounts.searchInOrganizations"
                    :options="allAccounts"
                    label="name"
                    value="id"
                    :multiple=true
                    placeholder="None"
                >
                  <template slot="option" slot-scope="option">
                    {{ option.name }}
                  </template>
                </vue-select>

                <span v-show="errors.has('settings_form.AccessLevel')" class="help-block">{{ errors.first('ap.participantType') }}</span>
              </div>

            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.hipaaRegulated') }">
                <label for="account-status" class="control-label">* Is account regulated by HIPAA? </label>
                <select class="form-control" id="account-hippa-regulated" v-model="accounts.HIPAARegulated" name="hipaaRegulated" data-vv-as="Status"
                        v-validate="'required|integer'">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
                <span v-show="errors.has('ap.hipaaRegulated')" class="help-block">{{ errors.first('ap.status') }}</span>
              </div>
            </div>

            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.twoFactorAuthEnabled') }">
                <label for="account-status" class="control-label">* Two-Step Verification? </label>
                <select class="form-control" id="two-factor-verification" v-model="accounts.twoFactorAuthEnabled" name="twoFactorAuthEnabled" data-vv-as="Status"
                        v-validate="'required|integer'">
                  <option value="0">Off</option>
                  <option value="1">On</option>
                </select>
                <span v-show="errors.has('ap.twoFactorAuthEnabled')" class="help-block">{{ errors.first('ap.twoFactorAuthEnabled') }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.expirationDate') }">
                <label for="account-status" class="control-label">* Select expiration date of this account</label>
                <date-picker id="account-expiration-date"
                             v-model="accounts.expirationDate"
                             name="expirationDate"
                             lang="en"
                             :value-type="'format'"
                             :confirm="false"
                             prefix-class="xmx"
                             v-validate="'required'"
                             data-vv-as="Expiration Date"
                             :format="calendarFormat"
                />
                <span v-show="errors.has('ap.expirationDate')" class="help-block">{{ errors.first('ap.expirationDate') }}</span>
              </div>
            </div>

          </div>

          <hr>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.organizationName') }">
                <label class="control-label">Organization Name:</label>
                <input data-vv-as="Organization Name" name="organizationName" v-model="accounts.organizationName"
                       type="text" class="form-control" v-validate="'required'">
                <span v-show="errors.has('ap.organizationName')"
                      class="help-block">{{ errors.first('ap.organizationName') }}</span>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label for="account-address1" class="control-label">Address 1:</label>
                <input id="account-address1" type="text" v-model="accounts.data.address1" class="form-control">
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group">
                <label for="account-address2" class="control-label">Address 2:</label>
                <input id="account-address2" type="text" v-model="accounts.data.address2" class="form-control">
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label for="account-city" class="control-label">City:</label>
                <input id="account-city" type="text" v-model="accounts.data.city" class="form-control">
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label for="account-state" class="control-label">State / Province / Region:</label>
                <input id="account-state" type="text" v-model="accounts.data.state" class="form-control">
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.zipCode') }">
                <label for="account-zip" class="control-label">ZIP / Postal Code:</label>
                <input id="account-zip" data-vv-as="ZIP / Postal Code" type="text" v-model="accounts.data.zipCode" class="form-control">
                <span v-show="errors.has('ap.zipCode')" class="help-block">{{ errors.first('ap.zipCode') }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label for="account-country" class="control-label">Country:</label>
                <input id="account-country" type="text" v-model="accounts.data.country" class="form-control">
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.billingContactName') }">
                <label class="control-label">Billing Contact Name:</label>
                <input data-vv-as="Billing Contact Name" name="billingContactName" v-model="accounts.data.billingContactName"
                       type="text" class="form-control" v-validate="'required'">
                <span v-show="errors.has('ap.billingContactName')" class="help-block">{{ errors.first('ap.billingContactName') }}</span>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.billingEmailAddress') }">
                <label for="account-billing-email" class="control-label">Billing Email Address:</label>
                <input id="account-billing-email" data-vv-as="Billing Email Address" name="billingEmailAddress"
                       v-model="accounts.data.billingEmailAddress" type="text" class="form-control" v-validate="'required|email'">
                <span v-show="errors.has('ap.billingEmailAddress')" class="help-block">{{ errors.first('ap.billingEmailAddress') }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="account-billing-phone" class="control-label">Billing Primary Phone:</label>
                <input id="account-billing-phone" type="text" class="form-control" v-model="accounts.data.billingPrimaryPhone">
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.contactName') }">
                <label for="account-billing-contact-name" class="control-label">Program Contact Name:</label>
                <input id="account-billing-contact-name" data-vv-as="Program Contact Name" name="contactName"
                       v-model="accounts.data.contactName" type="text" class="form-control" v-validate="'required'">
                <span v-show="errors.has('ap.contactName')" class="help-block">{{ errors.first('ap.contactName') }}</span>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.accountOwner') }">
                <label for="account-owner" class="control-label">Account Owner:</label>
                <input id="account-owner" data-vv-as="Account Owner" name="accountOwner"
                       v-model="accounts.data.accountOwner" type="text" class="form-control" v-validate="'required'">
                <span v-show="errors.has('ap.accountOwner')" class="help-block">{{ errors.first('ap.accountOwner') }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.projectContact') }">
                <label for="account-project-contact" class="control-label">Project Contact:</label>
                <input id="account-project-contact" data-vv-as="Project Contact" name="projectContact"
                       v-model="accounts.data.projectContact" type="text" class="form-control" v-validate="'required'">
                <span v-show="errors.has('ap.projectContact')" class="help-block">{{ errors.first('ap.projectContact') }}</span>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-7">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.emailAddress') }">
                <label for="account-contact-email" class="control-label">Program Contact Email Address:</label>
                <input id="account-contact-email" data-vv-as="Program Contact Email Address" name="emailAddress"
                       v-model="accounts.data.emailAddress" type="text" class="form-control" v-validate="'required'">
                <span v-show="errors.has('ap.emailAddress')" class="help-block">{{ errors.first('ap.emailAddress') }}</span>
              </div>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label for="account-program-primary-phone" class="control-label">Program Primary Phone:</label>
                <input id="account-program-primary-phone" type="text" class="form-control" name="phoneNumber" v-model="accounts.data.phoneNumber">
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="account-service-category" class="control-label">Service Category:</label>
                <select id="account-service-category" class="form-control" v-model="accounts.data.serviceCategory">
                  <option>Advocacy</option>
                  <option>Business & Professional Organizations</option>
                  <option>Civil Rights/Legal Activities</option>
                  <option>Community Activities</option>
                  <option>Conservation & Environmental Activities</option>
                  <option>Cultural or Historical Activities</option>
                  <option>Education Activities</option>
                  <option>Farming Activities</option>
                  <option>Health Services</option>
                  <option>Housing Activities</option>
                  <option>Legislative & Political Activities</option>
                  <option>Other Instructional & Training Activities</option>
                  <option>Recreational Activities</option>
                  <option>Religious Activities</option>
                  <option>Scientific Research Activities</option>
                  <option>Social Activities</option>
                  <option>Youth Activities</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label for="account-sms-status" class="control-label">SMS Status:</label>
                <select id="account-sms-status" class="form-control" name="twilioStatus" v-model="accounts.twilioStatus" :disabled="accounts.HIPAARegulated == 1">
                  <option :value="0">Disabled</option>
                  <option :value="1">Enabled</option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group" :class="{ 'has-error': errors.has('ap.twilioPhone') }">
                <label for="account-twilio-phone" class="control-label">Twilio Phone Number:</label>
                <input id="account-twilio-phone" type="text" class="form-control" name="twilioPhone" v-model="accounts.twilioPhone" :disabled="accounts.HIPAARegulated == 1">
                <span v-show="errors.has('ap.twilioPhone')" class="help-block">{{ errors.first('ap.twilioPhone') }}</span>
              </div>
            </div>
          </div>
          <div class="clearfix"></div>
          <div class="inline-buttons">
            <button class="btn green mt-ladda-btn ladda-button account-btn-generate" data-style="expand-left" v-if="$route.params.casemgrid == null" type="submit">
              <span class="ladda-label">Generate CaseMGR Account</span>
            </button>
            <button class="btn green mt-ladda-btn ladda-button account-btn-save" data-style="expand-left" v-if="$route.params.casemgrid != null">
              <span class="ladda-label">Save</span>
            </button>
            <router-link :to="{name:'Manage Account List'}" tag="button" class="btn default"
                         v-if="$route.params.casemgrid != null">
              Cancel
            </router-link>
            <router-link :to="{name:'Manage Account List'}" tag="button" class="btn green pull-right" v-if="user.access > 5">
              Account Directory
            </router-link>
            <button v-if="accounts.accountType === 'parent' && accounts.id" class="btn green pull-right margin-right-10" @click.prevent="linkAccountModal">Link Account</button>
          </div>
          <div class="clearfix"></div>
        </form>
      </div>
      <div class="tab-pane" :class="{active: activeTab == 'manage-users'}" id="update-access" v-show="$route.params.casemgrid != null">
        <manage-users-form></manage-users-form>
      </div>
      <div class="tab-pane" :class="{active: activeTab == 'linked-accounts'}" id="linked-accounts" v-if="['parent','child'].includes(accounts.accountType)">
        <template v-if="accounts.accountType == 'child' && !newAccount">
          <p style="padding-bottom: 3em">Changes to this account must be completed from the Parent Account:
            <strong><a :href="this.$router.resolve({name:'Manage Account Edit CaseMGR', params: {casemgrid: accounts.parentAccount.id}}).href">{{accounts.parentAccount.organizationName}}</a></strong>
          </p>
        </template>
        <linked-accounts-list v-else></linked-accounts-list>
      </div>
      <div class="tab-pane" :class="{active: activeTab == 'programs'}" id="programs">
        <programs v-on:program-created="programCreated"></programs>
      </div>

    </div>

    <div class="modal fade" id="link-account-modal" role="dialog" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" data-dismiss="modal" aria-hidden="true">×</button>
            <h4 class="modal-title">Link Account</h4></div>
          <div class="modal-body">
            Are you sure you would like to create a Child Account for this account?
          </div>
          <div class="modal-footer">
            <button class="btn green mt-ladda-btn ladda-button enable-linked-accounts-tab-btn" @click.prevent="enableLinkedAccountsTab" data-style="expand-left">
              <span class="ladda-label">Yes</span>
            </button>
            <button class="btn default" data-dismiss="modal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>
<script>
  import {mapActions, mapGetters} from 'vuex';
  import ManageUsersForm from '@/components/forms/ManageUsersForm';
  import LinkedAccountsList from "@/components/LinkedAccountsList";
  import Programs from '@/components/Programs'
  import DatePicker from 'vue2-datepicker';
  import {HTTP, getUserInfo} from '@/http-common'
  import vSelect from 'vue-select'

  export default {
    data() {
      return {
        newAccount: true,
        lockAccountType: false,
        allAccounts: [],
      }
    },
    inject: {
      $validator: '$validator'
    },
    components: {
      ManageUsersForm,
      LinkedAccountsList,
      Programs,
      DatePicker,
      'vue-select': vSelect
    },
    computed: {
      ...mapGetters({
        accounts: 'account',
        accountFromServer: 'accountFromServer',
        errorBag: 'accountErrorBag',
        user: 'getUser',
        parentAccount: 'parentAccount',
        participantDisplayNames: 'helper/getParticipantDisplayNames'
      }),
      calendarFormat() {
        return getUserInfo('date_format')
      },
      activationDate: {
        get: function () {
          if (this.accounts.activationDate != null)
            return this.$options.filters.formatDateModal(this.accounts.activationDate['date']);
        },
        set: function () {
        }
      },
      activeTab() {
        if (location.hash === '') {
          return 'account-profile';
        }
        return location.hash.replace('#', '');
      }
    },
    methods: {
      ...mapActions({
        addAccount: 'ADD_ACCOUNT_ACTION',
        getAccount: 'GET_ACCOUNT_ACTION',
        resetAccount: 'RESET_ACCOUNT_ACTION',
        saveAccount: 'SAVE_ACCOUNT_ACTION',
        setParentAccountId: 'SET_PARENT_ACCOUNT_ID_ACTION',
      }),
      renderErrors() {
        this.errors.clear('ap');
        Object.keys(this.errorBag).forEach(obj => {
          if (obj !== 'data') {
            this.errors.add(obj, this.errorBag[obj][0], 'auth', 'ap');
          }
        });
        if (this.errorBag['data'] != null) {
          Object.keys(this.errorBag['data']).forEach(obj => {
            this.errors.add(obj, this.errorBag['data'][obj][0], 'auth', 'ap');
          });
        }
      },
      onSubmit() {
        this.errors.clear('ap');
        this.$validator.validateAll('ap').then(result => {
          if (result) {
            if (this.$route.params.casemgrid == null) {
              let context = Ladda.create(document.querySelector('.account-btn-generate'));
              this.addAccount(context);
            } else {
              let context = Ladda.create(document.querySelector('.account-btn-save'));
              this.saveAccount({id: this.$route.params.casemgrid, context});
            }
          }
        });
      },
      linkAccountModal() {
        $('#link-account-modal').modal('show');
      },
      enableLinkedAccountsTab() {
        let context = Ladda.create(document.querySelector('.enable-linked-accounts-tab-btn'));
        this.accounts.accountType = 'parent';
        this.onSubmit();
        $('#link-account-modal').modal('hide');
      },
      updateAllAccountsList(clearSelected = false) {
        if (clearSelected) {
          this.accounts.searchInOrganizations = [];
        }
        if (!['program', 'default'].includes(this.accounts.accountType) || this.accounts.participantType === undefined) {
          this.allAccounts = [];
          return
        }
        HTTP.get('accounts/by-participant-type/' + this.accounts.participantType).then(response => {
          this.allAccounts = response.data.data.accounts;
        }).catch(err => {
          console.log(err);
        })
      },
      programCreated(program) {
        if (this.accounts.hasOwnProperty('programs')) {
          this.accounts.programs.push(program);
        } else {
          this.accounts.programs = [program];
        }
        this.lockAccountType = true;
      }
    },
    mounted() {
      this.errors.clear('ap');

      if (this.$route.params.casemgrid != null) {
        this.getAccount(this.$route.params.casemgrid);
        this.newAccount = false;
      } else {
        this.resetAccount();
      }

      if (this.$route.params.parentid != null) {
        this.resetAccount();
        this.lockAccountType = true;
        this.setParentAccountId(this.$route.params.parentid);
      }

    },
    watch: {
      errorBag: function () {
        this.renderErrors();
      },
      accounts: {
        handler(newData, oldData) {
          if (newData.HIPAARegulated === 1) {
            this.accounts.twilioStatus = 0;
            this.accounts.twilioPhone = '';
          }

          this.updateAllAccountsList();

          if (newData.accountType == 'program' && newData.hasOwnProperty('programs') && newData.programs.length) {
            this.lockAccountType = true;
          }

          if ((newData.accountType == 'parent' && newData.childrenAccounts && newData.childrenAccounts.length) || newData.accountType == 'child') {
            this.lockAccountType = true;
          }
        },
        deep: true
      },
      allAccounts: function (newVal) {
        if (!newVal.length) {
          return;
        }
        newVal.forEach(item => {
          let orgItem = this.accounts.searchInOrganizations.find(organization => organization.id == item.id)
          if (orgItem !== undefined) {
            orgItem.name = item.name;
          }
        })
      }
    }
  }
</script>
<style scoped>
  li.disabled {
    cursor: not-allowed;
  }

  li.disabled a {
    pointer-events: none;
    color: rgb(169, 168, 168) !important;
  }

  @media only screen and (max-width: 420px) {
    .inline-buttons .btn {
      margin-top: 10px;
    }

    .inline-buttons .pull-right {
      float: initial !important;
    }
  }

  .datepicker {
    width: 100%;
  }

  #link-account-modal .modal-dialog {
    width: 300px;
  }

</style>



// WEBPACK FOOTER //
// AccountCaseMGR.vue?4f45a95d